/* ----------------------------------------------------------------------------
 * BRO "Patient Data Collection" visit form — Visit 2+ (Follow-up) Chapters
 *
 * Mapped to the V2 components extracted from the production bundle:
 *   AnthropometricsSharedV2, VitalSignsSharedV2, SmokingHistorySharedV2,
 *   MorbiditiesSharedV2, MedicationSharedV2, QuestionnaireBroV2, CpapV2,
 *   BreathAnalysisSharedV2, Visit2FinishShared.
 * ------------------------------------------------------------------------- */
import { yesNo } from './bex'
import { vitalSigns } from './bro'
import { breathAnalysis } from './bex'

export const anthropometricsV2 = [
  { type: 'date', name: 'dateOfVisit', label: 'Date of Visit' },
  { type: 'date', name: 'dateOfBirth', label: 'Date of Birth' },
  {
    type: 'number',
    name: 'weight',
    label: 'Weight (kg)',
    placeholder: 'Weight (kg)',
    step: 'any',
    min: '0',
  },
  {
    type: 'number',
    name: 'height',
    label: 'Height (cm)',
    placeholder: 'Height (cm)',
    step: 'any',
    min: '0',
  },
]

export const smokingV2 = [
  {
    type: 'group',
    name: 'smoking',
    children: [
      {
        type: 'select',
        name: 'habits',
        label: 'Smoking habits (Visit 1)',
        options: {
          '': '',
          neverSmoker: 'Never-smoker',
          smoker: 'Smoker',
          exSmoker: 'Ex-smoker',
        },
      },
      {
        type: 'text',
        name: 'cigsPerDay',
        label: 'Number of cigarettes per day',
      },
      { type: 'text', name: 'packYears', label: 'Pack years' },
      { type: 'text', name: 'started', label: 'Start of smoking' },
    ],
  },
  {
    type: 'select',
    name: 'smokingV2.changeInSmokingHabits',
    label: 'Change in Smoking Habits',
    options: yesNo,
  },
  {
    type: 'group',
    name: 'smokingV2',
    children: [
      {
        type: 'select',
        name: 'habits',
        label: 'New Smoking habits',
        options: {
          '': '',
          neverSmoker: 'Never-smoker',
          smoker: 'Smoker',
          exSmoker: 'Ex-smoker',
        },
      },
      {
        type: 'text',
        name: 'cigsPerDay',
        label: 'Number of cigarettes per day',
      },
      { type: 'text', name: 'ended', label: 'End of smoking' },
      { type: 'text', name: 'packYears', label: 'Pack years' },
      { type: 'text', name: 'started', label: 'Start of smoking' },
    ],
  },
]

export const morbiditiesV2 = [
  {
    type: 'group',
    name: 'morbidities',
    children: [
      {
        type: 'icd',
        name: 'morbidity',
        label: 'Morbidity (Visit 1)',
        placeholder: 'Type in ICD code',
      },
    ],
  },
  {
    type: 'group',
    name: 'comorbidities',
    repeatable: true,
    'add-label': '+ Add comorbidity',
    children: [
      {
        type: 'icd',
        name: 'comorbidity',
        label: 'Comorbidity (Visit 1)',
        placeholder: 'Type in ICD code',
      },
    ],
  },
  {
    type: 'select',
    name: 'morbidity.changeInMorbidities',
    label: 'Change in Morbidities',
    options: yesNo,
  },
  {
    type: 'group',
    name: 'morbiditiesV2',
    children: [
      {
        type: 'icd',
        name: 'morbidity',
        label: 'New Morbidity',
        placeholder: 'Type in ICD code',
      },
    ],
  },
  {
    type: 'group',
    name: 'comorbiditiesV2',
    repeatable: true,
    'add-label': '+ Add new comorbidity',
    children: [
      {
        type: 'icd',
        name: 'comorbidity',
        label: 'New Comorbidity',
        placeholder: 'Type in ICD code',
      },
    ],
  },
  { type: 'textarea', name: 'comments', label: 'Comments' },
]

export const medicationV2 = [
  {
    type: 'group',
    name: 'medication',
    repeatable: true,
    'add-label': '+ Add medication',
    children: [
      {
        type: 'text',
        name: 'drugName',
        label: 'Drug (Trade Name)',
        placeholder: 'Drug (Trade Name)',
      },
      {
        type: 'text',
        name: 'activeSubstance',
        label: 'Active Substance',
        placeholder: 'Active Substance',
      },
      {
        type: 'number',
        name: 'dosagePerIntake',
        label: 'Dosage per Intake (mg)',
        placeholder: 'Dosage',
        min: '0',
        step: 'any',
      },
      {
        type: 'number',
        name: 'intakeMorning',
        label: 'Morning',
        help: 'Morning',
        min: '0',
        value: '0',
      },
      {
        type: 'number',
        name: 'intakeMidday',
        label: 'Midday',
        help: 'Midday',
        min: '0',
        value: '0',
      },
      {
        type: 'number',
        name: 'intakeEvening',
        label: 'Evening',
        help: 'Evening',
        min: '0',
        value: '0',
      },
      {
        type: 'number',
        name: 'intakeNight',
        label: 'Night',
        help: 'Night',
        min: '0',
        value: '0',
      },
      { type: 'datetime-local', name: 'lastIntake', label: 'Last intake' },
    ],
  },
  {
    type: 'select',
    name: 'changeInMedication',
    label: 'Change in Medication',
    options: yesNo,
  },
  {
    type: 'group',
    name: 'medicationV2',
    repeatable: true,
    'add-label': '+ Add new medication',
    children: [
      {
        type: 'drug',
        name: 'drugName',
        label: 'Drug (Trade Name)',
        placeholder: 'Drug (Trade Name)',
      },
      {
        type: 'text',
        name: 'activeSubstance',
        label: 'Active Substance',
        placeholder: 'Active Substance',
      },
      {
        type: 'number',
        name: 'dosagePerIntake',
        label: 'Dosage per Intake (mg)',
        placeholder: 'Dosage',
        min: '0',
        step: 'any',
      },
      {
        type: 'number',
        name: 'intakeMorning',
        label: 'Morning',
        help: 'Morning',
        min: '0',
        value: '0',
      },
      {
        type: 'number',
        name: 'intakeMidday',
        label: 'Midday',
        help: 'Midday',
        min: '0',
        value: '0',
      },
      {
        type: 'number',
        name: 'intakeEvening',
        label: 'Evening',
        help: 'Evening',
        min: '0',
        value: '0',
      },
      {
        type: 'number',
        name: 'intakeNight',
        label: 'Night',
        help: 'Night',
        min: '0',
        value: '0',
      },
      { type: 'datetime-local', name: 'lastIntake', label: 'Last intake' },
    ],
  },
]

export const questionnaireV2 = [
  { type: 'text', name: 'essTotalScore', label: 'ESS total score' },
  { type: 'text', name: 'fosqTotalScore', label: 'FOSQ total score' },
]

export const cpapTherapy = [
  {
    type: 'text',
    name: 'cpap.ahiUnderCpap',
    label: 'AHI under CPAP (number/h)',
    placeholder: 'AHI under CPAP',
  },
  {
    type: 'text',
    name: 'cpap.totalDaysofCpap',
    label: 'Total days of CPAP usage',
    placeholder: 'Total days of CPAP usage',
  },
  {
    type: 'text',
    name: 'cpap.daysWithLessThan4h',
    label: 'Days with < 4h usage',
    placeholder: 'Days with < 4h usage',
  },
  {
    type: 'text',
    name: 'cpap.odiUnderCpap',
    label: 'ODI under CPAP',
    placeholder: 'ODI under CPAP',
  },
  {
    type: 'text',
    name: 'cpap.medianUsageTime',
    label: 'Median usage time (hours)',
    placeholder: 'hh:mm',
  },
  {
    type: 'text',
    name: 'cpap.medianLeakage',
    label: 'Median leakage',
    placeholder: 'Median leakage',
  },
]

const finish = [{ component: 'h4', children: 'Visit 2 Completion' }]

export default {
  chapters: [
    {
      key: 'anthropometrics',
      name: 'Anthropometrics',
      schema: anthropometricsV2,
    },
    { key: 'vitalSigns', name: 'Vital signs', schema: vitalSigns },
    { key: 'smokingHistory', name: 'Smoking history', schema: smokingV2 },
    { key: 'morbidities', name: 'Morbidities', schema: morbiditiesV2 },
    { key: 'medication', name: 'Medication', schema: medicationV2 },
    { key: 'questionnaire', name: 'Questionnaire', schema: questionnaireV2 },
    { key: 'cpapTherapy', name: 'CPAP therapy', schema: cpapTherapy },
    { key: 'breathAnalysis', name: 'Breath analysis', schema: breathAnalysis },
    { key: 'finish', name: 'Visit completion', schema: finish },
  ],
}
